import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { ChevronLeft } from '@util/icons'
import * as Style from '@util/style'
import { mediaBreakpointUp } from '@util/ui'

interface IProps {
  backAction?: () => void
  artAndCopy: JSX.Element
  actions: JSX.Element
  fab?: JSX.Element
}

const Layout: React.FC<IProps> = ({ backAction, artAndCopy, actions, fab }) => {
  return (
    <ScrollJam>
      <Grid>
        <ArtAndCopy>{artAndCopy}</ArtAndCopy>
        <Actions>
          {!!backAction && (
            <BackButtonWrapper>
              <Button onClick={backAction}>
                <ChevronLeft height={19} /> Back
              </Button>
            </BackButtonWrapper>
          )}
          <Scroll>{actions}</Scroll>
          {!!fab && (
            <Fab>
              <FabOverlay />
              <FabContent>{fab}</FabContent>
            </Fab>
          )}
        </Actions>
      </Grid>
    </ScrollJam>
  )
}

const Grid = styled.div({
  display: 'flex',
  justifyContent: 'center',
  height: '100%',
  overflow: 'hidden',

  [mediaBreakpointUp('md')]: {
    height: 'auto', // Safari 13.1 bug requires no height on the grid
    display: 'grid',
    gridTemplateColumns: '5fr 7fr'
  }
})

const fadeIn = keyframes`
0% {
  opacity: 0
}
100% {
  opacity: 1
}
`

const ArtAndCopy = styled.div({
  display: 'none',
  animationName: fadeIn,
  animationDuration: '1s',
  animationTimingFunction: 'ease',

  [mediaBreakpointUp('md')]: {
    display: 'grid',
    placeItems: 'center',
    backgroundColor: Style.COLOR.GREY_100,
    overflow: 'hidden'
  }
})

const Actions = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#fff'
})

const BackButtonWrapper = styled.div({
  width: '100%',
  backgroundColor: '#fff',
  paddingTop: '1rem',
  paddingBottom: '0.5rem',

  [mediaBreakpointUp('md')]: {
    marginLeft: 0,
    paddingTop: '1rem',
    paddingBottom: '0.5rem'
  },
  [mediaBreakpointUp('lg')]: {
    marginLeft: '2rem',
    paddingTop: '1rem',
    paddingBottom: '0.5rem'
  }
})

const Button = styled.button({
  display: 'flex',
  alignItems: 'center',
  color: Style.COLOR.GREY_700,
  fontSize: '1rem',
  backgroundColor: 'transparent',
  border: 'none',
  textAlign: 'center',
  verticalAlign: 'middle',
  lineHeight: 1.5,
  transition: 'color 0.15s ease',
  paddingTop: '0.25rem',
  paddingBottom: '0.25rem',

  ':hover, :focus, :active': {
    color: Style.COLOR.GREY_800
  }
})

// Stop the scene from using body element to scroll the content
const ScrollJam = styled.div({
  display: 'grid',
  gridTemplateRows: '1fr',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: Style.COLOR.GREY_100
})

const Scroll = styled.div({
  flex: 1,
  overflowY: 'scroll',
  scrollBehavior: 'smooth',

  // Custom scrollbar for Chrome, Safari, Edge, Opera
  '&::-webkit-scrollbar': {
    width: 6,
    height: 8,
    backgroundColor: '#fff'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: Style.COLOR.GREY_300,
    borderRadius: 10
  }
})

const Fab = styled.div({
  position: 'relative',
  marginTop: 'auto'
})

const FabContent = styled.div({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  paddingRight: 6 // align with scroll styling
})

const FabOverlay = styled.div({
  pointerEvents: 'none',
  position: 'absolute',
  bottom: 0,
  height: '8rem',
  width: '100%',
  background:
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 100%)'
})

export default Layout
